import React, { useState, SVGProps } from "react";
import { Button, cn, Input } from "@adora/shadcn-ui";
import Logo from "../adora-logo";
import Auth, { AdoraUser } from "../../utils/auth";
import Result from "../../types/result";
import AadoraMonogram from "./adoraMonogram.svg";

type AadoraMonogramProps = SVGProps<SVGSVGElement>;
const StyledAadoraMonogram = AadoraMonogram as unknown as React.FC<AadoraMonogramProps>;

export interface LoginProps {
  onLogin: (user: Result<AdoraUser>) => void;
  subTitle?: string;
}

const Login: React.FC<LoginProps> = ({ onLogin, subTitle = "" }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSigninHappening, setIsSigninHappening] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSigninHappening(true);
    setError("");

    const result = await Auth.login(new FormData(event.currentTarget));
    if (result.isError() && result.error.message !== "Brand is not active") {
      setError("Invalid username or password");
    }

    onLogin(result);

    setIsSigninHappening(false);
  };

  /** Internal login passes `subTitle` (light blue panel); web uses dark `#222222` (Flux `secondary.main`). */
  const onLightBlueLeft = Boolean(subTitle);

  return (
    <div className="flex h-screen flex-col-reverse md:flex-row">
      <div
        className={cn(
          "relative flex h-[300px] md:h-screen w-full flex-col items-center justify-start overflow-hidden md:relative md:w-1/3 md:max-w-[428px] md:justify-center",
          onLightBlueLeft ? "bg-[#9ABDDC]" : "bg-[#222222]"
        )}
      >
        <div className="-translate-y-full text-center">
          <Logo width="243px" fill="#FFFFFF" />
        </div>
        <div className="absolute bottom-0 left-0 -translate-x-[14%] translate-y-[10%]">
          <Logo width="877px" fill="#FFFFFF" opacity={0.05} />
        </div>
        <div className="absolute bottom-[100px] left-5">
          <StyledAadoraMonogram width="96px" fill="#FFFFFF" />
        </div>
        <div className="absolute bottom-5 left-5 w-[250px]">
          <p className="font-poppins text-sm text-white">
            <span className="font-extrabold">Adora</span> is the AI performance marketing platform built for one brand:
            yours
          </p>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center p-4 h-full">
        <div className="p-8 pt-8 md:p-16">
          <div className="block md:hidden">
            <Logo width="164px" fill="#222222" />
          </div>
          <h1 className="font-instrument-serif text-[80px] leading-[80px] text-gray-900 hidden md:block">Welcome</h1>
          <div className="flex justify-center pt-2">
            {subTitle ? <p className="font-poppins text-base text-gray-600">{subTitle}</p> : null}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex min-w-[min(100vw-2rem,400px)] max-w-[400px] flex-col gap-6">
            <Input
              data-action-name="login username input"
              type="text"
              name="username"
              value={username}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
              placeholder="Username"
              autoComplete="username"
              className="h-12 rounded-full border border-gray-300 bg-background px-4 text-foreground placeholder:text-muted-foreground"
            />
            <Input
              data-action-name="login password input"
              type="password"
              name="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="h-12 rounded-full border border-gray-300 bg-background px-4 text-foreground placeholder:text-muted-foreground"
            />
            {error ? <p className="font-poppins text-sm text-red-600">{error}</p> : null}
            <Button
              data-action-name="login submit button"
              type="submit"
              variant="default"
              size="lg"
              disabled={isSigninHappening}
              className="h-12 w-full rounded-full border-none bg-gray-900 text-white hover:bg-gray-800 hover:text-white md:bg-gray-900 md:text-white md:hover:bg-gray-800 md:hover:text-white"
            >
              {isSigninHappening ? "Signing In..." : "Sign In"}
            </Button>
          </div>
        </form>

        <div className="pt-7">
          <a
            href="https://www.adora-ai.com/account-access"
            target="_blank"
            rel="noopener noreferrer"
            className="font-poppins text-sm text-[#0081eb] underline-offset-4 hover:underline"
          >
            Create Account
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
